import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { wallboardApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

const REFRESH_MS = 5_000;

const STATE_META = {
  ready:   { label: 'Ready',     color: '#3BD162' },
  aux:     { label: 'AUX',       color: '#F5A623' },
  wrap:    { label: 'Wrap-up',   color: '#2196F3' },
  on_call: { label: 'On Call',   color: '#9C27B0' },
  logout:  { label: 'Not Ready', color: '#9E9E9E' },
};

function fmt(s) {
  if (!s || s < 0) return '0s';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}h ${m}m` : `${m}m ${sec}s`;
}

export default function AgentPersonalView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['wallboard', 'personal'],
    queryFn:  wallboardApi.personal,
    refetchInterval: REFRESH_MS,
  });

  // Local stopwatch that ticks every second so the state timer feels alive
  // between server refreshes.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  if (error) return <div className="p-6 text-sm text-accent-red">Failed to load: {error.message}</div>;
  if (isLoading && !data) return <div className="p-6 text-sm text-surface-muted">Loading…</div>;
  if (!data) return null;

  const meta = STATE_META[data.state] || STATE_META.logout;
  const baseSecs = data.seconds_in_state || 0;
  // Add the local tick offset since the last server refresh
  const liveSecs = data.captured_at
    ? Math.max(baseSecs, baseSecs + Math.floor((Date.now() - new Date(data.captured_at).getTime()) / 1000))
    : baseSecs + tick;
  const overWrap = data.state === 'wrap' && liveSecs > 60;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">My ACD view</h2>
          <p className="text-xs text-surface-muted">Auto-refresh every {REFRESH_MS / 1000}s</p>
        </div>
        <div className="flex items-center gap-3">
          <HelpButton moduleKey="acd_personal" />
          <div className="text-right text-xs text-surface-muted">
            Ext <span className="font-mono">{data.extension}</span> · {data.name}
          </div>
        </div>
      </div>

      {/* Big state card */}
      <div
        className="rounded-thin border bg-surface-panel dark:bg-surface-dark-panel p-6 mb-4 shadow-thin-lg"
        style={{ borderColor: overWrap ? '#E53935' : meta.color }}
      >
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wider text-surface-muted">Current state</span>
            <span
              className="text-4xl font-semibold"
              style={{ color: overWrap ? '#E53935' : meta.color }}
            >{meta.label}</span>
            {data.reason && <span className="text-sm text-surface-muted mt-1">Reason: {data.reason}</span>}
          </div>
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wider text-surface-muted">Time in state</span>
            <span className={`text-4xl font-mono font-semibold ${overWrap ? 'text-accent-red' : ''}`}>{fmt(liveSecs)}</span>
            {overWrap && <span className="text-xs text-accent-red mt-1">Wrap exceeds 60s — consider going Ready</span>}
          </div>
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wider text-surface-muted">Calls handled today</span>
            <span className="text-4xl font-mono font-semibold">{data.calls_today}</span>
            <span className="text-xs text-surface-muted mt-1">
              In <span className="font-mono font-semibold text-surface-fg dark:text-surface-dark-fg">{data.calls_today_in ?? 0}</span>
              {' · '}Out <span className="font-mono font-semibold text-surface-fg dark:text-surface-dark-fg">{data.calls_today_out ?? 0}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Queues I'm a member of */}
      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4 shadow-thin">
        <h3 className="text-sm font-semibold mb-2">Queues I'm assigned to</h3>
        {data.queues.length === 0 ? (
          <p className="text-sm text-surface-muted">You're not currently a member of any portal-managed queues.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-surface-muted">
              <tr>
                <th className="text-left px-2 py-1">Queue</th>
                <th className="text-right px-2 py-1">Waiting</th>
                <th className="text-right px-2 py-1">Longest</th>
                <th className="text-right px-2 py-1">Today done</th>
                <th className="text-right px-2 py-1">SLA</th>
              </tr>
            </thead>
            <tbody>
              {data.queues.map((q) => (
                <tr key={q.queue_id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-2 py-1.5">
                    <span className="font-mono text-xs mr-2">{q.queue_id}</span>
                    {q.name}
                  </td>
                  <td className={`px-2 py-1.5 text-right font-mono ${q.callers > 0 ? 'text-accent-red font-semibold' : ''}`}>{q.callers}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(q.longest_wait)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{q.calls_completed}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{q.sla > 0 ? `${(q.sla * 100).toFixed(0)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data.last_call_id && (
        <p className="mt-3 text-xs text-surface-muted">Last call id: <span className="font-mono">{data.last_call_id}</span></p>
      )}
    </div>
  );
}
